import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const REMINDER_OPTIONS = [
    { value: 0, label: "No reminder" },
    { value: 1, label: "1 day before" },
    { value: 2, label: "2 days before" },
    { value: 3, label: "3 days before" },
    { value: 5, label: "5 days before" },
    { value: 7, label: "7 days before" },
];

const CreateMeeting = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [form, setForm] = useState({
        title: "",
        description: "",
        meeting_date: "",
        meeting_hour: "",
        meeting_minute: "00",
        meeting_ampm: "AM",
        reminder_days_before: 1,
    });
    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        if (!form.title.trim()) errs.title = "Title is required";
        else if (form.title.trim().length < 3) errs.title = "Minimum 3 characters";
        if (!form.meeting_date) errs.meeting_date = "Date is required";

        if (!form.meeting_hour) {
            errs.meeting_time = "Time is required";
        } else {
            let hour = parseInt(form.meeting_hour);
            if (form.meeting_ampm === "PM" && hour !== 12) hour += 12;
            if (form.meeting_ampm === "AM" && hour === 12) hour = 0;
            const timeStr = `${String(hour).padStart(2, "0")}:${form.meeting_minute}`;
            const dt = new Date(`${form.meeting_date}T${timeStr}`);
            if (dt <= new Date()) errs.meeting_time = "Must be a future date & time";
        }

        return errs;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((er) => ({ ...er, [name]: "", meeting_time: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) return setErrors(errs);

        let hour = parseInt(form.meeting_hour);
        if (form.meeting_ampm === "PM" && hour !== 12) hour += 12;
        if (form.meeting_ampm === "AM" && hour === 12) hour = 0;
        const timeStr = `${String(hour).padStart(2, "0")}:${form.meeting_minute}:00`;
        const meeting_date = `${form.meeting_date} ${timeStr}`;

        dispatch(loadingTrue());
        const res = await Handler({
            method: "POST",
            url: "/meetings/create.php",
            data: {
                title: form.title,
                description: form.description,
                meeting_date,
                reminder_days_before: parseInt(form.reminder_days_before),
            },
        });
        dispatch(loadingFalse());

        if (res.success) {
            toast.success("Meeting scheduled!");
            navigate("/user/meetings");
        } else {
            toast.error(res.message || "Failed to create meeting");
        }
    };

    const minDate = new Date().toISOString().split("T")[0];

    return (
        <MainLayout>
            <FeedLayout>
                <div className="mt-page">
                    <button className="mt-btn-back" onClick={() => navigate("/user/meetings")}>
                        <i className="bx bx-arrow-back" /> Back to Meetings
                    </button>

                    <div className="mt-header mt-2">
                        <div>
                            <h1 className="mt-title">Schedule Meeting</h1>
                            <p className="mt-subtitle">Set up a new meeting with date, time and reminder</p>
                        </div>
                    </div>

                    <div className="mt-form-wrap">
                        <form className="mt-form" onSubmit={handleSubmit}>

                            {/* Title */}
                            <div className="mt-field">
                                <label className="mt-label">Meeting Title <span className="mt-req">*</span></label>
                                <input
                                    className={`mt-input ${errors.title ? "mt-input-err" : ""}`}
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    placeholder="e.g. Q2 Strategy Discussion"
                                />
                                {errors.title && (
                                    <span className="mt-err-msg">
                                        <i className="bx bx-error-circle" /> {errors.title}
                                    </span>
                                )}
                            </div>

                            {/* Description */}
                            <div className="mt-field">
                                <label className="mt-label">Description</label>
                                <textarea
                                    className="mt-textarea"
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Agenda, participants, objectives..."
                                    rows={4}
                                />
                            </div>

                            {/* Date + Time row */}
                            <div className="mt-field-row">
                                <div className="mt-field">
                                    <label className="mt-label">Date <span className="mt-req">*</span></label>
                                    <input
                                        type="date"
                                        className={`mt-input ${errors.meeting_date ? "mt-input-err" : ""}`}
                                        name="meeting_date"
                                        value={form.meeting_date}
                                        onChange={handleChange}
                                        min={minDate}
                                    />
                                    {errors.meeting_date && (
                                        <span className="mt-err-msg">
                                            <i className="bx bx-error-circle" /> {errors.meeting_date}
                                        </span>
                                    )}
                                </div>

                                {/* Time — 12hr format */}
                                <div className="mt-field">
                                    <label className="mt-label">Time <span className="mt-req">*</span></label>
                                    <div style={{ display: "flex", gap: "8px" }}>

                                        {/* Hour */}
                                        <select
                                            className={`mt-input ${errors.meeting_time ? "mt-input-err" : ""}`}
                                            name="meeting_hour"
                                            value={form.meeting_hour}
                                            onChange={handleChange}
                                        >
                                            <option value="">HH</option>
                                            {Array.from({ length: 12 }, (_, i) => {
                                                const h = String(i + 1).padStart(2, "0");
                                                return <option key={h} value={h}>{h}</option>;
                                            })}
                                        </select>

                                        {/* Minute */}
                                        <select
                                            className="mt-input"
                                            name="meeting_minute"
                                            value={form.meeting_minute}
                                            onChange={handleChange}
                                        >
                                            {["00", "15", "30", "45"].map((m) => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>

                                        {/* AM / PM */}
                                        <select
                                            className="mt-input"
                                            name="meeting_ampm"
                                            value={form.meeting_ampm}
                                            onChange={handleChange}
                                        >
                                            <option value="AM">AM</option>
                                            <option value="PM">PM</option>
                                        </select>

                                    </div>
                                    {errors.meeting_time && (
                                        <span className="mt-err-msg">
                                            <i className="bx bx-error-circle" /> {errors.meeting_time}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Reminder */}
                            <div className="mt-field">
                                <label className="mt-label">
                                    <i className="bx bx-bell" style={{ color: "#2563eb", marginRight: 6 }} />
                                    Reminder
                                </label>
                                <div className="mt-reminder-grid">
                                    {REMINDER_OPTIONS.map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={`mt-reminder-chip-btn ${parseInt(form.reminder_days_before) === opt.value ? "active" : ""}`}
                                        >
                                            <input
                                                type="radio"
                                                name="reminder_days_before"
                                                value={opt.value}
                                                checked={parseInt(form.reminder_days_before) === opt.value}
                                                onChange={handleChange}
                                                style={{ display: "none" }}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="mt-form-actions">
                                <button type="button" className="mt-btn-outline" onClick={() => navigate("/user/meetings")}>
                                    Cancel
                                </button>
                                <button type="submit" className="mt-btn-primary">
                                    <i className="bx bx-calendar-plus" /> Schedule Meeting
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </FeedLayout>
        </MainLayout>
    );
};

export default CreateMeeting;